import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { Badge, Button, Dialog, Switch, useToast, type BadgeTone } from '@artosera/ui';
import { basculerModule, obtenirModules } from '../../lib/api.js';
import { dependancesErreur, raisonErreur, useLibellesModules } from '../../lib/modules.js';
import { usePeut } from '../../auth/usePeut.js';
import type { GalerieFiche, ModuleVue, ReponseModules } from '../../types.js';
import pageStyles from '../../styles/page.module.css';
import styles from './ModulesTab.module.css';

type EtatAffiche = 'actif' | 'inactif' | 'coupe' | 'bientot' | 'enAttente';

const TONE_ETAT: Record<EtatAffiche, BadgeTone> = {
  actif: 'available',
  inactif: 'neutral',
  coupe: 'sold',
  bientot: 'withdrawn',
  enAttente: 'reserved',
};

/**
 * Ce que la console dit d’un module optionnel, dans cet ordre de priorité.
 * `bientot` garde l’étape `disponible: false` d’un module en construction
 * (docs/dev/modules.md § 7). Les 18 compléments et 2 services sont disponibles
 * au 4 octobre 2026 ; l’état reste pour le prochain module en construction,
 * que l’API refuse avec `module_non_disponible`.
 */
function etatAffiche(module: ModuleVue): EtatAffiche {
  if (!module.disponible) return 'bientot';
  if (module.coupeGlobalement) return 'coupe';
  if (module.effectif) return 'actif';
  if (module.active) return 'enAttente';
  return 'inactif';
}

const RAISONS_CONNUES = [
  'module_coeur',
  'module_non_disponible',
  'galerie_resiliee',
  'revision_modules_perimee',
  'journal_indisponible',
] as const;

/** La clé de requête des modules d'une galerie, sous celle de sa fiche. */
function cleModules(idGalerie: string): readonly unknown[] {
  return ['console', 'galerie', idGalerie, 'modules'];
}

/**
 * Onglet « Modules » (D21, ARTO-56). Le registre suit la page commerciale
 * « Composez votre Artosera » : le cœur, affiché verrouillé ; les modules
 * complémentaires et les services (site, boutique), qui se basculent un à un.
 * Activer est immédiat ; désactiver passe par une confirmation qui rappelle
 * que rien n'est supprimé. Le palier est une donnée commerciale : il ne
 * change aucun module.
 */
export function ModulesTab({
  idGalerie,
  galerie,
}: {
  idGalerie: string;
  galerie: GalerieFiche;
}): JSX.Element {
  const { t } = useTranslation('console');
  const peut = usePeut();
  // Résiliée, en purge ou purgée (ARTO-54) : les modules ne se règlent plus.
  const resiliee =
    galerie.etat === 'resiliee' || galerie.etat === 'purge_en_cours' || galerie.etat === 'purgee';
  const peutGerer = peut('console:manage-modules') && !resiliee;
  const libelles = useLibellesModules();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [erreur, setErreur] = useState<string | null>(null);
  const [aDesactiver, setADesactiver] = useState<ModuleVue | null>(null);

  const requete = useQuery({
    queryKey: cleModules(idGalerie),
    queryFn: () => obtenirModules(idGalerie),
  });

  const messageErreur = (cause: unknown): string => {
    const raison = raisonErreur(cause);
    if (raison === 'dependances_manquantes') {
      return t('fiche.modules.erreurs.dependances_manquantes', {
        liste: libelles.liste(dependancesErreur(cause)),
      });
    }
    if (raison && (RAISONS_CONNUES as readonly string[]).includes(raison)) {
      return t(`fiche.modules.erreurs.${raison}`);
    }
    return t('fiche.modules.erreurs.generique');
  };

  const apresMutation = async (reponse: ReponseModules): Promise<void> => {
    queryClient.setQueryData(cleModules(idGalerie), reponse);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['console', 'galerie', idGalerie] }),
      queryClient.invalidateQueries({ queryKey: ['console', 'galeries'] }),
    ]);
  };

  const bascule = useMutation({
    mutationFn: ({ module, actif }: { module: string; actif: boolean }) =>
      basculerModule(idGalerie, module, actif),
    onMutate: () => setErreur(null),
    onSuccess: async (reponse, { module, actif }) => {
      setADesactiver(null);
      await apresMutation(reponse);
      toast({
        title: actif
          ? t('fiche.modules.succesActive', { module: libelles.libelle(module) })
          : t('fiche.modules.succesDesactive', { module: libelles.libelle(module) }),
        closeLabel: t('fiche.modules.fermer'),
      });
    },
    onError: (cause) => {
      setADesactiver(null);
      setErreur(messageErreur(cause));
    },
  });

  if (requete.isLoading) {
    return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  }
  if (requete.isError || !requete.data) {
    return (
      <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
        {t('fiche.modules.erreurChargement')}
      </p>
    );
  }

  const { modules, modulesActifs } = requete.data;
  const coeur = modules.filter((module) => module.famille === 'coeur');
  const complementaires = modules.filter((module) => module.famille === 'complementaire');
  const services = modules.filter((module) => module.famille === 'service');

  const surBascule = (module: ModuleVue, actif: boolean): void => {
    if (actif) {
      bascule.mutate({ module: module.id, actif: true });
    } else {
      setADesactiver(module);
    }
  };

  const dependantsActifs = aDesactiver
    ? aDesactiver.dependants.filter((dependant) => modulesActifs.includes(dependant))
    : [];

  return (
    <div className={styles.onglet}>
      {erreur ? (
        <p role="alert" className={`${pageStyles.banniere} ${pageStyles.bannierePeril}`}>
          {erreur}
        </p>
      ) : null}

      {resiliee ? (
        <p className={styles.note}>{t('fiche.modules.resiliee')}</p>
      ) : !peutGerer ? (
        <p className={styles.note}>{t('fiche.modules.lectureSeule')}</p>
      ) : null}

      <section className={pageStyles.panneau} aria-labelledby="modules-coeur-titre">
        <h2 id="modules-coeur-titre" className={styles.titreSection}>
          {t('fiche.modules.titreCoeur')}
        </h2>
        <p className={styles.texteSection}>{t('fiche.modules.texteCoeur')}</p>
        <ul className={styles.grilleCoeur}>
          {coeur.map((module) => (
            <li key={module.id} className={styles.carteCoeur} data-module={module.id}>
              <Lock className={styles.cadenas} size={16} strokeWidth={1.5} aria-hidden="true" />
              <div className={styles.corpsModule}>
                <div className={styles.enteteModule}>
                  <span className={styles.nomModule}>{libelles.libelle(module.id)}</span>
                  <Badge>{t('fiche.modules.inclus')}</Badge>
                </div>
                <p className={styles.description}>{libelles.description(module.id)}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {(
        [
          ['complementaires', complementaires, 'titreComplementaires', 'texteComplementaires'],
          ['services', services, 'titreServices', 'texteServices'],
        ] as const
      ).map(([cle, liste, titre, texte]) => (
        <section key={cle} className={pageStyles.panneau} aria-labelledby={`modules-${cle}-titre`}>
          <h2 id={`modules-${cle}-titre`} className={styles.titreSection}>
            {t(`fiche.modules.${titre}`)}
          </h2>
          <p className={styles.texteSection}>{t(`fiche.modules.${texte}`)}</p>
          <ul className={styles.listeComplementaires}>
            {liste.map((module) => {
              const etat = etatAffiche(module);
              const bloqueParDependance = !module.active && module.dependancesManquantes.length > 0;
              const enCours = bascule.isPending && bascule.variables?.module === module.id;
              const notes: string[] = [];
              if (module.dependances.length > 0) {
                notes.push(
                  t('fiche.modules.necessite', { liste: libelles.liste(module.dependances) }),
                );
              }
              if (module.dependants.length > 0) {
                notes.push(
                  t('fiche.modules.dependants', {
                    count: module.dependants.length,
                    liste: libelles.liste(module.dependants),
                  }),
                );
              }
              if (!module.disponible) {
                notes.push(t('fiche.modules.bientotTexte'));
              } else if (module.coupeGlobalement) {
                notes.push(t('fiche.modules.coupeTexte'));
              } else if (module.active && !module.effectif) {
                const manquants = module.dependances.filter(
                  (dependance) => !modulesActifs.includes(dependance),
                );
                notes.push(t('fiche.modules.eteint', { liste: libelles.liste(manquants) }));
              } else if (bloqueParDependance && peutGerer) {
                notes.push(
                  t('fiche.modules.activerDabord', {
                    liste: libelles.liste(module.dependancesManquantes),
                  }),
                );
              }
              const idDescription = `module-${module.id}-description`;
              return (
                <li key={module.id} className={styles.ligneModule} data-module={module.id}>
                  <div className={styles.enteteModule}>
                    <Switch
                      id={`module-${module.id}`}
                      label={
                        <span className={styles.nomModule}>{libelles.libelle(module.id)}</span>
                      }
                      checked={module.active}
                      disabled={!peutGerer || !module.disponible || bloqueParDependance || enCours}
                      onCheckedChange={(actif) => surBascule(module, actif)}
                      aria-describedby={idDescription}
                    />
                    <Badge tone={TONE_ETAT[etat]}>{t(`fiche.modules.etat.${etat}`)}</Badge>
                  </div>
                  <div id={idDescription} className={styles.corpsComplementaire}>
                    <p className={styles.description}>{libelles.description(module.id)}</p>
                    {notes.length > 0 ? (
                      <ul className={styles.notes}>
                        {notes.map((note) => (
                          <li key={note}>{note}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <Dialog
        open={aDesactiver !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setADesactiver(null);
        }}
        title={t('fiche.modules.desactiverTitre', {
          module: aDesactiver ? libelles.libelle(aDesactiver.id) : '',
        })}
        description={t('fiche.modules.desactiverTexte')}
        closeLabel={t('fiche.modules.fermer')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setADesactiver(null)}>
              {t('fiche.modules.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={bascule.isPending}
              onClick={() => {
                if (aDesactiver) bascule.mutate({ module: aDesactiver.id, actif: false });
              }}
            >
              {bascule.isPending ? t('fiche.modules.enCours') : t('fiche.modules.desactiver')}
            </Button>
          </>
        }
      >
        {aDesactiver?.id === 'facturation' ? (
          <p className={styles.texteDialogue}>{t('fiche.modules.desactiverVentes')}</p>
        ) : null}
        {aDesactiver?.id === 'site' ? (
          <p className={styles.texteDialogue}>{t('fiche.modules.desactiverSite')}</p>
        ) : null}
        {dependantsActifs.length > 0 ? (
          <p className={styles.texteDialogue}>
            {t('fiche.modules.desactiverDependants', {
              liste: libelles.liste(dependantsActifs),
            })}
          </p>
        ) : null}
      </Dialog>
    </div>
  );
}
